import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import type { TemplateStarringAccountItem } from '@app/insights/types'

export type AccountsDrawerContext = {
  columnName: string
  ledgerId: number
  accountDetailTemplateId: number
}

// Snake_case: raw payload of the template_starring_accounts endpoint.
export type AccountsDrawerDetails = {
  company_name: string
  period: string
  template_name: string
  total_count: number
  starred_accounts: TemplateStarringAccountItem[]
  not_starred_accounts: TemplateStarringAccountItem[]
}

export const useAccountsListDrawer = defineStore(
  'insights-accounts-list-drawer',
  () => {
    const columnName = ref('')
    const details = ref<AccountsDrawerDetails | null>(null)
    const loading = ref(false)
    const failed = ref(false)
    // id of the table row whose cell opened the drawer, so it can be highlighted
    const activeRowId = ref<string | number | null>(null)

    // Discards responses of superseded requests: clicking another row while a
    // fetch is in flight must not let the slow response overwrite the new row.
    let requestToken = 0

    // Last opened context/row, retained so refresh() can refetch the same
    // cell's data when the companies query is reloaded.
    let lastContext: AccountsDrawerContext | null = null
    let lastRowId: string | number | null = null

    async function open(
      context: AccountsDrawerContext,
      rowId: string | number | null = null
    ): Promise<void> {
      lastContext = context
      lastRowId = rowId
      columnName.value = context.columnName
      activeRowId.value = rowId
      details.value = null
      failed.value = false
      loading.value = true
      const token = ++requestToken

      try {
        const data = (await api.fetchTemplateStarringAccounts({
          ledgerId: context.ledgerId,
          accountDetailTemplateId: context.accountDetailTemplateId,
        })) as AccountsDrawerDetails

        if (token !== requestToken) return
        details.value = data
      } catch {
        if (token !== requestToken) return
        failed.value = true
      } finally {
        if (token === requestToken) {
          loading.value = false
        }
      }
    }

    // Refetches the currently-shown cell's data. No-op unless the drawer is
    // open (has content, is loading, or previously failed) so a query reload
    // does not spuriously reopen a closed drawer.
    function refresh(): void {
      if (!lastContext) return
      if (!details.value && !loading.value && !failed.value) return
      void open(lastContext, lastRowId)
    }

    function clear(): void {
      // Bumping the token invalidates any in-flight fetch (its finally checks
      // token equality and skips the reset), so reset loading/failed here
      // synchronously rather than relying on finally.
      requestToken++
      columnName.value = ''
      details.value = null
      loading.value = false
      failed.value = false
      activeRowId.value = null
      lastContext = null
      lastRowId = null
    }

    return {
      columnName,
      details,
      loading,
      failed,
      activeRowId,
      open,
      refresh,
      clear,
    }
  }
)
